import { FC, Fragment } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { StepEnum } from 'src/features/step-list/constants';
import { YourBookingDetails } from 'src/features/your-details/booking-details';
import { theme } from 'src/styles/theme';
import { StepList } from 'src/features/step-list';
import { useFetchCalculateBookingDetails } from 'src/lib/hooks/use-fetch-calculate-booking-details';
import { Spinner } from 'src/components/spinner';
import { useFetchCreatePaymentPlan } from 'src/lib/hooks/use-create-payment-plan';
import { InstalmentType } from 'src/shared/enum/InstalmentType';
import { getTermByInstalmentId } from 'src/utils/get-term-by-instalment-id';
import { useRebookerFlow } from 'src/utils/get-is-rebooker';

import { UserFormContainer } from '../../features/your-details/user-form/user-form.container';
import { DiscountBanner } from '../../components/discount-banner';
import { ReferralCodeCard } from '../../features/referral-code';
import { RoomCard, RoomCardMobileOverview } from '../../features/room-card';
import { PaymentPlan, PaymentPlanMobileOverview } from '../../features/payment-plan';
import { Currency } from '../../shared/enum/Currency';
import { AgreementsContainer } from '../agreements';
import { RoommatesContainer } from '../multi-occupancy';
import { routes } from '../../constants/routes';
import { ApiBasket } from '../../api/basketService';

import { styles } from './styles';

export const BookingFlowContainer: FC<{ step: StepEnum; basket: ApiBasket }> = ({
    step,
    basket,
}) => {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const product = basket.products?.[0];

    const roomCode = product.room_code;
    const academicYear = product.academic_year;
    const isOriaRoom = roomCode.includes('OR1');

    let paymentPlanId = null;

    if (product.stl) {
        paymentPlanId = product.payment_plan_id;
    }
    const isRebooker = params.get('rebooker') === 'true';

    const { isValidRebooker } = useRebookerFlow(roomCode, isRebooker);

    const {
        data: calculatedBookingDetails,
        isLoading: isCalculating,
        isError: isCalculateError,
    } = useFetchCalculateBookingDetails(roomCode, academicYear, paymentPlanId);

    const userChoices = product
        ? {
              numberOfStudents: product.selected_students,
              term: product.term,
              instalmentType: product.instalment_type as InstalmentType,
              instalmentId: product.payment_plan_id,
          }
        : null;

    const selectedTerm = getTermByInstalmentId(
        calculatedBookingDetails?.bookingDetails?.terms,
        userChoices?.instalmentId
    );

    const {
        data: createdPaymentPlan,
        isFetching: fetchingPaymentPlan,
        isError: failedToCreatePaymentPlan,
    } = useFetchCreatePaymentPlan({
        userChoices,
        promotionId: isValidRebooker?.promotion_id ? isValidRebooker.promotion_id : null,
        isRebooker,
        fillerWeekEligible: false,
        roomData: calculatedBookingDetails?.room ?? null,
        bookingId: null,
    });

    const hasDiscount = !!product?.promotion_id;
    const currency = product?.currency as Currency;

    if (fetchingPaymentPlan || isCalculating || fetchingPaymentPlan) {
        return (
            <Box css={styles.layout}>
                <Box css={styles.leftSide}>
                    <Spinner />
                </Box>
            </Box>
        );
    }

    if (
        !basket ||
        isCalculateError ||
        failedToCreatePaymentPlan ||
        !createdPaymentPlan ||
        !calculatedBookingDetails ||
        !userChoices ||
        !selectedTerm
    ) {
        navigate(routes.failure.index);
        return null;
    }

    const renderStep = () => {
        switch (step) {
            case StepEnum.YourDetails:
                return (
                    <UserFormContainer
                        building={calculatedBookingDetails.building}
                        basket={basket}
                    />
                );
            case StepEnum.Roommates:
                return (
                    <RoommatesContainer
                        basket={basket}
                        numberOfStudents={userChoices.numberOfStudents}
                    />
                );
            case StepEnum.Agreements:
                return (
                    <AgreementsContainer
                        bookingDetails={calculatedBookingDetails}
                        building={calculatedBookingDetails.building}
                        basket={basket}
                        roomData={calculatedBookingDetails.room}
                        userChoices={{ ...userChoices, term: selectedTerm }}
                        createdPaymentPlan={createdPaymentPlan}
                    />
                );
            case StepEnum.Payment:
                return <Box>Payment</Box>;
            default:
                return null;
        }
    };

    return (
        <Box css={styles.layout}>
            <Box css={styles.leftSide}>
                <StepList
                    activeStep={step}
                    paymentRequired={calculatedBookingDetails.room.advance_payment_weeks > 0}
                />
                {step === StepEnum.YourDetails && (
                    <Fragment>
                        <RoomCardMobileOverview
                            roomData={calculatedBookingDetails.room}
                            building={calculatedBookingDetails.building}
                            termDuration={selectedTerm.duration}
                            termStartDate={selectedTerm.startDate}
                            termEndDate={selectedTerm.endDate}
                            numberOfStudents={userChoices.numberOfStudents}
                        />

                        <YourBookingDetails
                            calculatedBookingDetails={calculatedBookingDetails}
                            userChoices={{ ...userChoices, term: selectedTerm }}
                            basket={basket}
                        />

                        <PaymentPlanMobileOverview
                            hasDiscount={hasDiscount}
                            currency={currency}
                            createdPaymentPlan={createdPaymentPlan}
                        />

                        {isSmallScreen && <ReferralCodeCard basket={basket} />}
                    </Fragment>
                )}
                {renderStep()}
            </Box>
            <Box css={styles.rightSide}>
                <RoomCard
                    withSmallImage
                    roomData={calculatedBookingDetails.room}
                    isAgreementsPage={step === StepEnum.Agreements}
                    building={calculatedBookingDetails.building}
                    termDuration={selectedTerm.duration}
                    termStartDate={selectedTerm.startDate}
                    termEndDate={selectedTerm.endDate}
                    numberOfStudents={userChoices.numberOfStudents}
                />
                <ReferralCodeCard basket={basket} />
                <Box css={styles.paymentPlanWrapper}>
                    {hasDiscount && (
                        <DiscountBanner>
                            <Typography variant="body3">{t('discount_applied_info')}</Typography>
                        </DiscountBanner>
                    )}
                    <Box css={styles.overviewPayment}>
                        <PaymentPlan
                            showCta={false}
                            withHeaderStipe={false}
                            currency={currency}
                            disabled={false}
                            createdPaymentPlan={createdPaymentPlan}
                            isGapWeekEligible={createdPaymentPlan.fillerWeekEligible}
                            showSantanderCards={isOriaRoom}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
